import JournalTrends from '../JournalTrends';
import RecoveryTracker from '../RecoveryTracker';
import ProBenchmarks from '../ProBenchmarks';
import ProAthleteComparison from '../ProAthleteComparison';
import FitnessCalculator from '../FitnessCalculator';
import UKWHOGrowthCharts from '../UKWHOGrowthCharts';
import PerformanceTrends from '../PerformanceTrends';
import FitnessDashboard from '../FitnessDashboard';
import TechnicalSkills from '../TechnicalSkills';
import InjuryPrevention from '../InjuryPrevention';
import BadgesDisplay from '../BadgesDisplay';
import GPSTracking from '../GPSTracking';
import GPSComparison from '../GPSComparison';
import HighlightReel from '../HighlightReel';
import WattbikeData from '../WattbikeData';
import { BarChart3 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../config/api';

export default function TrendsTab({ childId, childName, childData, measurements, athleticTests, coachRatings }) {
  const [gpsSessions, setGpsSessions] = useState([]);
  const [videos, setVideos] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [gpsRes, videosRes] = await Promise.all([
        axios.get(`${API}/gps-tracking/sessions/${childId}`).catch(() => ({ data: { sessions: [] } })),
        axios.get(`${API}/training-plan/videos/${childId}`).catch(() => ({ data: { videos: [] } }))
      ]);
      setGpsSessions(gpsRes.data.sessions || []);
      setVideos(videosRes.data.videos || []);
    } catch (error) {
      // Ignore errors
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6" data-testid="trends-tab">
      {/* Achievement Badges */}
      <BadgesDisplay 
        childId={childId}
        childName={childName}
      />
      
      {/* Highlight Reels - Video Compilation */}
      <HighlightReel 
        childId={childId}
        videos={videos}
      />
      
      {/* Injury Prevention - AI-powered risk assessment */}
      <InjuryPrevention 
        childId={childId}
        childName={childName}
      />
      
      {/* GPS Tracking - Action Tracer, Playermaker, etc. */}
      <GPSTracking 
        childId={childId}
        childName={childName}
      />
      
      {/* GPS Session Comparison */}
      <GPSComparison 
        childId={childId}
        sessions={gpsSessions}
      />
      
      {/* Journal Trends - Session RPE, Performance, Mood */}
      <JournalTrends 
        childId={childId}
        childName={childName}
      />
      
      {/* Recovery & Wellness Tracker */}
      <RecoveryTracker childId={childId} />
      
      {/* Pro Academy Benchmarks */}
      <ProBenchmarks childId={childId} childData={childData} />
      
      {/* Pro Athlete Comparison - TheSportsDB */}
      <ProAthleteComparison childId={childId} childData={childData} />
      
      {/* Fitness Calculator */}
      <FitnessCalculator childId={childId} childData={childData} />
      
      {/* UK-WHO Growth Charts */}
      <UKWHOGrowthCharts 
        childId={childId}
        childName={childName}
      />
      
      {/* Fitness Dashboard (Strava, Fitbit, WeFitter) */}
      <FitnessDashboard 
        childId={childId} 
        childName={childName} 
      />
      
      {/* Wattbike Indoor Cycling Data */}
      <WattbikeData 
        childId={childId}
        childName={childName}
      />
      
      {/* Technical Skills Section - Sport-specific grading */}
      <TechnicalSkills 
        childId={childId}
        sport={childData?.primary_sport}
        childName={childName}
      />
      
      {/* Performance Trends */}
      <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="h-6 w-6 text-[#02b5ff]" />
          <h2 className="text-lg font-bold uppercase tracking-widest text-white">Performance Trends</h2>
        </div>
        <PerformanceTrends 
          athleticTests={athleticTests || []}
          coachRatings={coachRatings || []}
          childName={childName}
        />
      </div>
    </div>
  );
}
